import {
  $createTextNode,
  $getSelection,
  $isNodeSelection,
  $isRangeSelection,
  $isTextNode,
  $setSelection,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_NORMAL,
  FORMAT_TEXT_COMMAND,
  INDENT_CONTENT_COMMAND,
  INSERT_LINE_BREAK_COMMAND,
  INSERT_PARAGRAPH_COMMAND,
  KEY_ARROW_RIGHT_COMMAND,
  KEY_TAB_COMMAND,
  OUTDENT_CONTENT_COMMAND,
  REDO_COMMAND,
  UNDO_COMMAND
} from "lexical"
import { CodeNode } from "@lexical/code"
import { $createAutoLinkNode, $toggleLink, LinkNode } from "@lexical/link"
import { $getNearestNodeOfType, $insertNodeToNearestRoot } from "@lexical/utils"
import { INSERT_TABLE_COMMAND } from "@lexical/table"

import { $isLinkableAttachmentNode } from "../nodes/action_text_attachment_node"
import { createElement } from "../helpers/html_helper"
import { ListenerBin, registerEventListener } from "../helpers/listener_helper"
import { $normalizeBlockContainerSelection, getListType } from "../helpers/lexical_helper"
import { HorizontalDividerNode } from "../nodes/horizontal_divider_node"
import { REMOVE_HIGHLIGHT_COMMAND, TOGGLE_HIGHLIGHT_COMMAND } from "../extensions/highlight_extension"

const COMMANDS = [
  "bold",
  "italic",
  "strikethrough",
  "underline",
  "link",
  "unlink",
  "toggleHighlight",
  "removeHighlight",
  "setFormatHeadingLarge",
  "setFormatHeadingMedium",
  "setFormatHeadingSmall",
  "setFormatParagraph",
  "applyHeadingFormat",
  "clearFormatting",
  "insertUnorderedList",
  "insertOrderedList",
  "insertQuoteBlock",
  "insertCodeBlock",
  "setCodeLanguage",
  "insertHorizontalDivider",
  "uploadImage",
  "uploadFile",

  "insertTable",

  "undo",
  "redo"
]

export class CommandDispatcher {
  #selectionBeforeDrag = null
  #listeners = new ListenerBin()

  static configureFor(editorElement) {
    return new CommandDispatcher(editorElement)
  }

  constructor(editorElement) {
    this.editorElement = editorElement
    this.editor = editorElement.editor
    this.selection = editorElement.selection
    this.contents = editorElement.contents

    this.#registerCommands()
    this.#registerKeyboardCommands()
    this.#registerDragAndDropHandlers()
  }

  dispatchBold() {
    this.editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")
  }

  dispatchItalic() {
    this.editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")
  }

  dispatchStrikethrough() {
    this.editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough")
  }

  dispatchUnderline() {
    this.editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline")
  }

  dispatchToggleHighlight(styles) {
    this.editor.dispatchCommand(TOGGLE_HIGHLIGHT_COMMAND, styles)
  }

  dispatchRemoveHighlight() {
    this.editor.dispatchCommand(REMOVE_HIGHLIGHT_COMMAND)
  }

  dispatchLink(url) {
    this.editor.update(() => {
      this.selection.placeCursorInsideAdjacentLink()

      const selection = $getSelection()

      if ($isNodeSelection(selection)) {
        $setHrefOnSelectedImages(selection, url)
      } else if ($isRangeSelection(selection)) {
        $linkSelectedText(url, selection)
      }
    })
  }

  dispatchUnlink() {
    this.editor.update(() => {
      this.selection.placeCursorInsideAdjacentLink()

      // Let adapters signal whether unlink should target a frozen link key.
      if (this.editorElement.adapter.unlinkFrozenNode?.()) {
        return
      }

      const selection = $getSelection()

      if ($isNodeSelection(selection)) {
        $setHrefOnSelectedImages(selection, null)
      } else {
        $toggleLink(null)
      }
    })
  }

  dispatchInsertUnorderedList() {
    const selection = $getSelection()
    if (!$isRangeSelection(selection)) return

    const anchorNode = selection.anchor.getNode()

    if (this.selection.isInsideList && anchorNode && getListType(anchorNode) === "bullet") {
      this.contents.applyParagraphFormat()
    } else {
      this.contents.applyUnorderedListFormat()
    }
  }

  dispatchInsertOrderedList() {
    const selection = $getSelection()
    if (!$isRangeSelection(selection)) return

    const anchorNode = selection.anchor.getNode()

    if (this.selection.isInsideList && anchorNode && getListType(anchorNode) === "number") {
      this.contents.applyParagraphFormat()
    } else {
      this.contents.applyOrderedListFormat()
    }
  }

  dispatchInsertQuoteBlock() {
    this.contents.toggleBlockquote()
  }

  dispatchInsertCodeBlock() {
    if (this.#shouldToggleInlineCode()) {
      this.#toggleInlineCode()
    } else if (this.editorElement.supportsCodeBlocks) {
      this.contents.toggleCodeBlock()
    }
  }

  #shouldToggleInlineCode() {
    if (!this.editorElement.supportsInlineCode) {
      return false
    } else if (!this.editorElement.supportsCodeBlocks) {
      return true
    } else {
      return this.selection.hasSelectedWordsInSingleLine
    }
  }

  #toggleInlineCode() {
    const selection = $getSelection()
    if (!$isRangeSelection(selection)) return

    if (!selection.isCollapsed()) {
      const segments = this.#selectedTextSegments(selection)
      const applyingCode = !segments.every(({ node }) => node.hasFormat("code"))

      if (applyingCode) {
        this.#stripInlineFormattingFromSegments(selection, segments)
      }
    }

    this.editor.dispatchCommand(FORMAT_TEXT_COMMAND, "code")
  }

  // The text nodes in the selection with the offsets of their selected text.
  // Nodes the selection only touches at an edge, with no characters selected,
  // are left out.
  #selectedTextSegments(selection) {
    const [ startPoint, endPoint ] = this.#orderedPoints(selection)
    const textNodes = selection.getNodes().filter($isTextNode)

    return textNodes.map((node, i) => {
      const startOffset = i === 0 && startPoint.type === "text" ? startPoint.offset : 0
      const endOffset = i === textNodes.length - 1 && endPoint.type === "text" ? endPoint.offset : node.getTextContentSize()
      return { node, startOffset, endOffset }
    }).filter(({ startOffset, endOffset }) => startOffset !== endOffset)
  }

  // Strip all inline formatting (bold, italic, etc.) from the selected text so
  // that applying code produces a single merged <code> element instead of one
  // per differently-formatted span.
  #stripInlineFormattingFromSegments(selection, segments) {
    const [ startPoint, endPoint ] = this.#orderedPoints(selection)

    for (const { node, startOffset, endOffset } of segments) {
      if (node.getFormat() === 0) continue

      if (startOffset === 0 && endOffset === node.getTextContentSize()) {
        node.setFormat(0)
      } else {
        const holdsStart = startPoint.type === "text" && startPoint.key === node.getKey()
        const holdsEnd = endPoint.type === "text" && endPoint.key === node.getKey()

        const splits = node.splitText(startOffset, endOffset)
        const target = startOffset === 0 ? splits[0] : splits[1]
        target.setFormat(0)

        if (holdsStart) startPoint.set(target.getKey(), 0, "text")
        if (holdsEnd) endPoint.set(target.getKey(), endOffset - startOffset, "text")
      }
    }
  }

  #orderedPoints(selection) {
    return selection.isBackward() ? [ selection.focus, selection.anchor ] : [ selection.anchor, selection.focus ]
  }

  dispatchSetCodeLanguage(language) {
    this.editor.update(() => {
      if (!this.selection.isInsideCodeBlock) return

      const codeNode = this.selection.nearestNodeOfType(CodeNode)
      if (!codeNode) return

      codeNode.setLanguage(language)
    })
  }

  dispatchInsertHorizontalDivider() {
    $insertNodeToNearestRoot(new HorizontalDividerNode)
  }

  dispatchSetFormatHeadingLarge() {
    this.#applyConfiguredHeadingFormat(0)
  }

  dispatchSetFormatHeadingMedium() {
    this.#applyConfiguredHeadingFormat(1)
  }

  dispatchSetFormatHeadingSmall() {
    this.#applyConfiguredHeadingFormat(2)
  }

  dispatchSetFormatParagraph() {
    this.contents.applyParagraphFormat()
  }

  dispatchApplyHeadingFormat(tag) {
    this.contents.applyHeadingFormat(tag)
  }

  dispatchClearFormatting() {
    this.contents.clearFormatting()
  }

  dispatchUploadImage() {
    this.#dispatchUploadAttachment("image/*,video/*")
  }

  dispatchUploadFile() {
    this.#dispatchUploadAttachment()
  }

  #dispatchUploadAttachment(accept = null) {
    const attributes = {
      type: "file",
      multiple: true,
      style: "display: none;",
      onchange: ({ target: { files } }) => {
        this.contents.uploadFiles(files, { selectLast: true })
      }
    }

    if (accept) attributes.accept = accept

    const input = createElement("input", attributes)

    // Append and remove to make testable
    this.editorElement.appendChild(input)
    input.click()
    setTimeout(() => input.remove(), 1000)
  }

  dispatchInsertTable() {
    this.editor.dispatchCommand(INSERT_TABLE_COMMAND, { "rows": 3, "columns": 3, "includeHeaders": true })
  }

  dispatchUndo() {
    this.editor.dispatchCommand(UNDO_COMMAND, undefined)
  }

  dispatchRedo() {
    this.editor.dispatchCommand(REDO_COMMAND, undefined)
  }

  dispose() {
    this.#listeners.dispose()
  }

  #applyConfiguredHeadingFormat(index) {
    const tag = this.editorElement.config.get("headings")[index]
    if (tag) this.contents.applyHeadingFormat(tag)
  }

  #registerCommands() {
    for (const command of COMMANDS) {
      const methodName = `dispatch${capitalize(command)}`
      this.#registerCommandHandler(command, 0, this[methodName].bind(this))
    }
  }

  #registerCommandHandler(command, priority, handler) {
    this.#listeners.track(this.editor.registerCommand(command, handler, priority))
  }

  #registerKeyboardCommands() {
    this.#registerCommandHandler(KEY_ARROW_RIGHT_COMMAND, COMMAND_PRIORITY_NORMAL, this.#handleArrowRightKey.bind(this))
    this.#registerCommandHandler(KEY_TAB_COMMAND, COMMAND_PRIORITY_NORMAL, this.#handleTabKey.bind(this))

    // Run before Lexical's built-in insert handlers to descend an element point on a
    // block container to a leaf, avoiding error #211 on Enter / Shift+Enter in a quote.
    this.#registerCommandHandler(INSERT_LINE_BREAK_COMMAND, COMMAND_PRIORITY_HIGH, this.#normalizeBlockContainerSelection.bind(this))
    this.#registerCommandHandler(INSERT_PARAGRAPH_COMMAND, COMMAND_PRIORITY_HIGH, this.#normalizeBlockContainerSelection.bind(this))
  }

  #normalizeBlockContainerSelection() {
    $normalizeBlockContainerSelection()
    return false
  }

  #handleArrowRightKey(event) {
    const selection = $getSelection()
    if (!$isRangeSelection(selection) || !selection.isCollapsed()) return false
    if (this.selection.isInsideCodeBlock || !selection.hasFormat("code")) return false

    const anchorNode = selection.anchor.getNode()
    if (!$isTextNode(anchorNode) || selection.anchor.offset !== anchorNode.getTextContentSize()) return false
    if (anchorNode.getNextSibling() !== null) return false

    event.preventDefault()
    selection.toggleFormat("code")
    return true
  }

  #registerDragAndDropHandlers() {
    if (this.editorElement.supportsAttachments) {
      this.dragCounter = 0
      this.#listeners.track(
        this.editor.registerRootListener((rootElement) => {
          if (rootElement) {
            const teardowns = [
              registerEventListener(rootElement, "dragover", this.#handleDragOver.bind(this)),
              registerEventListener(rootElement, "drop", this.#handleDrop.bind(this)),
              registerEventListener(rootElement, "dragenter", this.#handleDragEnter.bind(this)),
              registerEventListener(rootElement, "dragleave", this.#handleDragLeave.bind(this))
            ]
            return () => teardowns.forEach((teardown) => teardown())
          }
        })
      )
    }
  }

  #handleDragEnter(event) {
    if (this.#isInternalDrag(event)) return

    this.dragCounter++
    if (this.dragCounter === 1) {
      this.#saveSelectionBeforeDrag()
      this.editor.getRootElement().classList.add("lexxy-editor--drag-over")
    }
  }

  #handleDragLeave(event) {
    if (this.#isInternalDrag(event)) return

    this.dragCounter--
    if (this.dragCounter === 0) {
      this.#selectionBeforeDrag = null
      this.editor.getRootElement().classList.remove("lexxy-editor--drag-over")
    }
  }

  #handleDragOver(event) {
    if (this.#isInternalDrag(event)) return

    event.preventDefault()
  }

  #handleDrop(event) {
    if (this.#isInternalDrag(event)) return

    event.preventDefault()

    this.dragCounter = 0
    this.editor.getRootElement().classList.remove("lexxy-editor--drag-over")

    const dataTransfer = event.dataTransfer
    if (!dataTransfer) return

    const files = Array.from(dataTransfer.files)
    if (!files.length) return

    this.#restoreSelectionBeforeDrag()
    this.contents.uploadFiles(files, { selectLast: true })

    this.editor.focus()
  }

  #saveSelectionBeforeDrag() {
    this.editor.getEditorState().read(() => {
      this.#selectionBeforeDrag = $getSelection()?.clone()
    })
  }

  #restoreSelectionBeforeDrag() {
    if (!this.#selectionBeforeDrag) return

    this.editor.update(() => {
      $setSelection(this.#selectionBeforeDrag)
    })

    this.#selectionBeforeDrag = null
  }

  #isInternalDrag(event) {
    return event.dataTransfer?.types.some((type) => type.startsWith("application/x-lexxy-"))
  }

  #handleTabKey(event) {
    if (this.selection.isInsideList) {
      return this.#handleTabForList(event)
    } else if (this.selection.isInsideCodeBlock) {
      return this.#handleTabForCode()
    }
    return false
  }

  #handleTabForList(event) {
    if (event.shiftKey && !this.selection.isIndentedList) return false

    event.preventDefault()
    const command = event.shiftKey? OUTDENT_CONTENT_COMMAND : INDENT_CONTENT_COMMAND
    return this.editor.dispatchCommand(command)
  }

  #handleTabForCode() {
    const selection = $getSelection()
    return $isRangeSelection(selection) && selection.isCollapsed()
  }

}

function $setHrefOnSelectedImages(selection, href) {
  for (const node of selection.getNodes()) {
    if ($isLinkableAttachmentNode(node)) node.getWritable().href = href
  }
}

function $linkSelectedText(url, selection) {
  const anchorNode = selection.anchor.getNode()

  if (selection.isCollapsed() && !$getNearestNodeOfType(anchorNode, LinkNode)) {
    const autoLinkNode = $createAutoLinkNode(url)
    const textNode = $createTextNode(url)
    autoLinkNode.append(textNode)
    selection.insertNodes([ autoLinkNode ])
  } else {
    $toggleLink(url)
  }
}

function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1)
}
